// src/components/SSIDomainQuestionPanel.js
import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/solid';
// import { ChevronLeftIcon, ChevronRightIcon, MicrophoneIcon, StopIcon } from '@heroicons/react/solid';
import { auth } from '../../../../firebase';
import { TriggerGenerateDocumentSection } from '../../../../utils/TriggerGenerateDocumentSection';
import { serverTimestamp } from 'firebase/firestore';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import DictationTextArea from '../../../../components/DictationTextArea';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import { buildFirstGeneratedFields, buildFirstCompletedFields, appendReportAuditEntry } from '../../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../../utils/appointmentDateHelpers';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { useSectionExitRegeneration } from '../../../../hooks/useSectionExitRegeneration';


export default function SSIDomainQuestionPanel({
  currentStageTitle,
  currentSectionTitle,
  currentSectionNumber,
  questions,
  question,
  index,
  selections,
  notes,
  activeRecording,
  handleSelection,
  handleNoteChange,
  handleRecording,
  jumpToQuestion,
  patientDataModel,
  fetchPatientData,
  updatePatientFirestore,
  patientId,
  navigate,
  textAreaRefs,
  canEdit,
  // Optional path/label overrides for the read-only SSI snapshot viewer so
  // navigation stays inside /ssi-snapshot/... and the exit CTA is not
  // "Go to Review & Synthesis" (which is not part of the snapshot).
  proformaNavOverride = null,
}) {
  
  const { flushPendingUpdates } = useContext(PatientDataContext);

  // Track whether any domain data has been modified since the panel mounted.
  // Prevents unnecessary Cloud Function calls when the clinician navigates
  // forward without making changes (the backend would skip LLM calls anyway,
  // but this avoids the Firestore read/write overhead of the Cloud Function).
  const [hasChanges, setHasChanges] = useState(false);

  // Safety-net: fires regeneration on unmount if the clinician exits via
  // breadcrumb/topnav without reaching the "Go to RDD/DD" button. Also
  // registers dirty state in the navigation guard so the confirmation
  // dialog appears when the SSI module was previously completed.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: currentStageTitle,
    sectionTitle: currentSectionTitle,
    hasChanges,
    canEdit,
    patientDataModel,
  });

  // Get claims from useAuth hook - no token refresh needed, always fresh
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // Replace static skipRDD with state
  const [skipRDD, setSkipRDD] = useState(false);
  
  // Add state for validation dialog
  const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
  const [validationResults, setValidationResults] = useState({
    missingSymptoms: [],
    missingDomains: [],
    hasAnyMissing: false
  });
  
  // Check organisation claims to determine if RDD should be skipped
  // Using useAuth claims ensures fresh data without force refresh, preventing quota errors
  useEffect(() => {
    // Guard: wait for claims to be available
    if (!claims?.organisation) {
      setSkipRDD(false); // Default to not skipping RDD
      return;
    }
    
    // Check if organization is "The Spectrum Mind Clinic"
    if (claims.organisation.includes("The Spectrum Mind Clinic")) {
      setSkipRDD(true);
    } else {
      setSkipRDD(false);
    }
  }, [claims?.organisation]);

  // Derive the forward-navigation label based on workflow:
  //   - Snapshot viewer: custom exit label (e.g. back to patient overview)
  //   - Spectrum Mind Clinic (skipRDD): skip RDD entirely → Diagnostic Decision
  //   - SSI Review orgs (TOC, SLaM, etc.): go to Review & Synthesis
  //   - All others: standard RDD flow
  const nextSectionLabel = proformaNavOverride?.afterDomainsLabel
    ? proformaNavOverride.afterDomainsLabel
    : skipRDD
      ? "Go to Diagnostic Decision"
      : wf.showSSIReviewPage
        ? "Go to Review & Synthesis"
        : "Go to Recommended Diagnosis";

  const handleNextSection = useCallback((route) => {
    const user = auth.currentUser;
    if (!user || !user.uid) return;

    markRegenHandled();

    const dataReady = flushPendingUpdates({ noTimeout: true });

    const generated = patientDataModel?.dataModel?.['6_manageReports']
      ?.sections?.['2']?.stageSectionsGenerated || [];
    const isGenerated = generated.includes(currentSectionTitle);

    if (canEdit && (hasChanges || !isGenerated)) {
      TriggerGenerateDocumentSection(patientId, 'Semi-Structured Interview', currentSectionTitle, [], dataReady);
    }

    navigate(route);
  }, [patientId, navigate, currentSectionTitle, canEdit, flushPendingUpdates,
      markRegenHandled, hasChanges, patientDataModel]);

  // Add validation function
  const validateCompletedAssessments = useCallback(() => {
    const results = {
      missingSymptoms: [],
      missingDomains: [],
      hasAnyMissing: false
    };

    // Get symptoms section data
    const symptomsSection = patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections?.['1'];
    if (symptomsSection?.questions) {
      Object.entries(symptomsSection.questions).forEach(([key, symptom]) => {
        const hasAdulthoodSelection = symptom.symptomPresent_Adulthood !== null && symptom.symptomPresent_Adulthood !== undefined;
        const hasChildhoodSelection = symptom.symptomPresent_Childhood !== null && symptom.symptomPresent_Childhood !== undefined;
        
        if (!hasAdulthoodSelection || !hasChildhoodSelection) {
          results.missingSymptoms.push({
            key: parseInt(key),
            dsmCode: symptom.DSM_code,
            symptom: symptom.DSM_symptom,
            missingAdulthood: !hasAdulthoodSelection,
            missingChildhood: !hasChildhoodSelection
          });
        }
      });
    }

    // Get domains section data (current section)
    const domainsSection = patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections?.['2'];
    if (domainsSection?.questions) {
      Object.entries(domainsSection.questions).forEach(([key, domain]) => {
        const hasAdulthoodSelection = domain.functioningReduced_Adulthood !== null && domain.functioningReduced_Adulthood !== undefined;
        const hasChildhoodSelection = domain.functioningReduced_Childhood !== null && domain.functioningReduced_Childhood !== undefined;
        
        if (!hasAdulthoodSelection || !hasChildhoodSelection) {
          results.missingDomains.push({
            key: parseInt(key),
            domain: domain.DSM_symptom,
            missingAdulthood: !hasAdulthoodSelection,
            missingChildhood: !hasChildhoodSelection
          });
        }
      });
    }

    results.hasAnyMissing = results.missingSymptoms.length > 0 || results.missingDomains.length > 0;
    return results;
  }, [patientDataModel]);

  const proceedToNextSection = useCallback(() => {
    if (canEdit) {
      updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: {
              status: 'completed',
              progress: 100,
            },
          },
        },
      });
    }

    // Snapshot viewer: leave the archived proforma (no live Review / RDD).
    if (proformaNavOverride?.afterDomainsPath) {
      handleNextSection(proformaNavOverride.afterDomainsPath);
      return;
    }

    if (skipRDD) {
      // Spectrum Mind Clinic: skip both RDD and SSI Review, go straight to DD
      if (canEdit) {
        const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
        const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['3_semiStructuredInterview']);

        updatePatientFirestore(patientId, {
          '3_semiStructuredInterview': {
            status: 'completed',
            progress: 100,
            docs_available: true,
            lastCompletedAt: serverTimestamp(),
            lastCompletedBy: auth.currentUser.uid,
            lastCompletedByName: auth.currentUser.displayName,
            ...firstCompletedFields,
          },
          '6_manageReports': {
            ssiReportGenerated: true,
            ssiReportGeneratedAt: serverTimestamp(),
            ssiReportGeneratedBy: auth.currentUser.uid,
            ssiReportGeneratedByName: auth.currentUser.displayName,
            ...buildFirstGeneratedFields('ssi', manageReports),
          }
        });

        appendReportAuditEntry(patientId, {
          module: 'ssi',
          eventType: 'generated',
          isFirstOccurrence: !manageReports?.ssiReportFirstGeneratedAt,
        });

        // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
        recordInferredAppointmentDateOnCompletion({
          patientId,
          moduleKey: 'ssi',
          didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
          enabled: wf.isAppointmentDatesEnabled,
        });
      }

      handleNextSection(`/patient/${patientId}/proforma/diagnostic-decision/primary-diagnosis-no-rdd`);
    } else if (wf.showSSIReviewPage) {
      // SSI Review orgs (TOC, SLaM, etc.): go to Review & Synthesis instead of RDD
      handleNextSection(`/patient/${patientId}/proforma/semi-structured-interview/review`);
    } else {
      handleNextSection(`/patient/${patientId}/proforma/semi-structured-interview/recommended-diagnosis`);
    }
  }, [
    canEdit,
    skipRDD,
    wf.showSSIReviewPage,
    wf.isAppointmentDatesEnabled,
    patientId,
    currentSectionNumber,
    updatePatientFirestore,
    handleNextSection,
    patientDataModel?.dataModel,
    proformaNavOverride,
  ]);

  const handleBackToSymptoms = useCallback(() => {
    const symptomsPath =
      proformaNavOverride?.symptomsPath ||
      `/patient/${patientId}/proforma/semi-structured-interview/symptoms`;
    handleNextSection(symptomsPath);
  }, [handleNextSection, patientId, proformaNavOverride]);

  const handleNavigateToNextSection = useCallback(() => {
    const validation = validateCompletedAssessments();

    if (validation.hasAnyMissing) {
      setValidationResults(validation);
      setIsValidationDialogOpen(true);
    } else {
      proceedToNextSection();
    }
  }, [validateCompletedAssessments, proceedToNextSection]);

  // Function to navigate to first missing symptom
  const navigateToFirstMissingSymptom = useCallback(() => {
    if (validationResults.missingSymptoms.length > 0) {
      const firstMissingSymptom = validationResults.missingSymptoms[0];
      const symptomsPath =
        proformaNavOverride?.symptomsPath ||
        `/patient/${patientId}/proforma/semi-structured-interview/symptoms`;
      navigate(symptomsPath, {
        state: { jumpToQuestion: firstMissingSymptom.key - 1 }
      });
    }
  }, [validationResults, navigate, patientId, proformaNavOverride]);

  // Function to navigate to first missing domain
  const navigateToFirstMissingDomain = useCallback(() => {
    if (validationResults.missingDomains.length > 0) {
      const firstMissingDomain = validationResults.missingDomains[0];
      // Jump to the specific domain question on current page
      jumpToQuestion(firstMissingDomain.key - 1);
      setIsValidationDialogOpen(false);
    }
  }, [validationResults, jumpToQuestion]);

  const handleSelectionWithFirestore = (index, type, value) => {
    if (!canEdit) return;
    // Check if we're clicking an already selected button (toggling off)
    const isUnselecting = selections[index][type] === value;
    
    // Call the parent component's selection handler
    handleSelection(index, type, value);
    
    const fieldToUpdate = type === "adulthood" ? 'functioningReduced_Adulthood' :
      type === "childhood" ? 'functioningReduced_Childhood' : null;
    
    // Update Firestore with null if unselecting, otherwise with the new value
    updatePatientFirestore(patientId, {
      '3_semiStructuredInterview': {
        sections: {
          [currentSectionNumber]: {
            questions: {
              [index + 1]: {
                [fieldToUpdate]: isUnselecting ? null : value,
              }
            }
          },
        },
      },
    });    
    
    setHasChanges(true);
    fetchPatientData(patientId);
  };

  const debounceTimeoutRef = useRef(null);

  // Persists the current note value to Firestore on blur (debounced).
  //
  // IMPORTANT: this handler intentionally does NOT call setHasChanges(true).
  // Dirty-tracking is handled exclusively by the onChange handler on the
  // DictationTextArea. The blur handler's sole job is data persistence.
  // Combining the two caused false-positive dirty checks: when the clinician
  // navigated between questions, the DictationTextArea's unmount safety-net
  // fired onBlur even though no text was edited, which incorrectly flagged
  // the page as dirty and triggered the navigation guard.
  const handleNoteBlur = async (index, type) => {
    if (!canEdit) return;

    // Skip the write if the note hasn't changed from what Firestore already
    // has (avoids a wasted write when the user merely focused the textarea
    // without typing, or when the unmount safety-net fires on navigation).
    const currentValue = notes[index]?.[type.toLowerCase()] ?? '';
    const storedValue = questions[index]?.[`clinicianNotes_${type}`] || '';
    if (currentValue === storedValue) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(async () => {
      await updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: {
              questions: {
                [index + 1]: {
                  [`clinicianNotes_${type}`]: notes[index][type.toLowerCase()]
                }
              }
            }
          }
        }
      });
    }, 500); // Debounce delay
  };
  
  // Initialize selectedExamples as an array of Sets for each question
  const [selectedExamples, setSelectedExamples] = useState(() =>
    questions.map(q => ({
      adulthood: new Set(q.examples_Adulthood || []),
      childhood: new Set(q.examples_Childhood || [])
    }))
  );

  // Handle checkbox changes
  const handleCheckboxChange = (type, example) => {
    if (!canEdit) return;

    setSelectedExamples(prev => {
      const updatedSet = new Set(prev[index][type]);
      if (updatedSet.has(example)) {
        updatedSet.delete(example);
      } else {
        updatedSet.add(example);
      }

      // Return the updated set for the specific question
      const updatedExamples = [...prev];
      updatedExamples[index] = { ...prev[index], [type]: updatedSet };

      // Update Firestore with the current selections
      const firestoreUpdate = {
        examples_Adulthood: Array.from(updatedExamples[index].adulthood),
        examples_Childhood: Array.from(updatedExamples[index].childhood)
      };

      updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: {
              questions: {
                [index + 1]: firestoreUpdate
              }
            }
          }
        }
      }).then(() => {
        fetchPatientData(patientId);
      });

      setHasChanges(true);
      return updatedExamples;
    });
  };

  return (
    <>
      <div key={index} id={`question-${index}`} className="bg-white p-6 rounded-lg mb-6">

        {/* Primary navigation: two main CTAs (back/forward in sequence).
            Skip links below provide a subtle shortcut to jump across sections
            without cluttering the button row. */}
        <div className="flex justify-between mb-3">
          {/* Left: single back CTA + optional skip link to Symptoms */}
          <div className="flex flex-col items-start">
            {index === 0 ? (
              <button
                onClick={handleBackToSymptoms}
                className="flex items-center py-2 px-4 mb-1 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
                aria-label="Back to Symptoms"
              >
                <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                <span className="font-bold">Back to Symptoms</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => jumpToQuestion(index - 1)}
                  disabled={!!activeRecording}
                  className="flex items-center py-2 px-4 mb-1 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
                  aria-label="Previous Domain"
                >
                  <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                  <span className="font-bold">Previous Domain</span>
                </button>
                <button
                  onClick={handleBackToSymptoms}
                  className="flex items-center text-xs text-gray-500 hover:text-violet-700 font-medium hover:underline transition-all duration-200 mt-1.5 group"
                  aria-label="Go to Symptoms"
                >
                  <ArrowLeftIcon className="w-3 h-3 mr-1 text-gray-400 group-hover:text-violet-700 transition-all duration-200" aria-hidden="true" />
                  <span>Go to Symptoms</span>
                </button>
              </>
            )}
          </div>

          {/* Right: single forward CTA + optional skip link to RDD/DD */}
          <div className="flex flex-col items-end">
            <button
              onClick={() => {
                if (index === questions.length - 1) {
                  handleNavigateToNextSection();
                } else {
                  jumpToQuestion(index + 1);
                }
              }}
              disabled={!!activeRecording}
              className="flex items-center py-2 px-4 mb-1 rounded-md shadow-md transition duration-100 bg-violet-900 text-white hover:bg-violet-800"
              aria-label={index === questions.length - 1 ? nextSectionLabel : "Next Domain"}
            >
              <span className="font-bold">
                {index === questions.length - 1 ? nextSectionLabel : "Next Domain"}
              </span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
            {index < questions.length - 1 && (
              <button
                onClick={handleNavigateToNextSection}
                className="flex items-center text-xs text-gray-500 hover:text-violet-700 font-medium hover:underline transition-all duration-200 mt-1.5 group"
                aria-label={nextSectionLabel}
              >
                <span>{nextSectionLabel}</span>
                <ArrowRightIcon className="w-3 h-3 ml-1 text-gray-400 group-hover:text-violet-700 transition-all duration-200" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mb-3">  
        <h2 className="flex items-center gap-2 text-gray-900 text-xl font-semibold leading-9 break-words">
      {question.DSM_symptom}
    </h2>     
  </div>      

       {/* ADULTHOOD */}
       <div className="flex mb-4">
          <div className="w-full text-left">
            <div className="w-full p-6 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-center gap-2 inline-flex">
              <div className="self-stretch flex-col justify-start items-center flex">
                <div className="self-stretch justify-start items-center gap-4 inline-flex">
                  <div className="grow shrink basis-0 self-stretch justify-start items-center gap-1 flex">
                    <div className="text-slate-700 text-xl font-semibold leading-loose">Adulthood (for at least 6 months)</div>
                  </div>
                  <div className="self-stretch justify-center items-center flex">
                    <div className="h-10 justify-start items-center flex">
                      <div className="grow shrink basis-0 h-9 justify-center items-center gap-3 flex">
                        <div className="flex-col justify-center items-center inline-flex">
                          <div className="text-slate-700 text-lg font-medium leading-normal">Moderate or severe impairment:</div>
                        </div>
                        <div className="grow shrink basis-0 h-9 justify-start items-center gap-3 flex">
                        <button
                            onClick={() => handleSelectionWithFirestore(index, 'adulthood', true)}
                            className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                              ${selections[index].adulthood === true ? 'bg-green-200 border-gray-600 border-2' : 'bg-white'} 
                              ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''} 
                              transition-all duration-300`}
                            disabled={!!activeRecording || !canEdit}
                            aria-label="Moderate or severe impairment present in adulthood: Yes"
                          >
                            <div className="px-0.5 justify-center items-center flex">
                              <div className="text-slate-700 text-sm font-semibold leading-tight">Yes</div>
                            </div>
                          </button>
                          <button
                            onClick={() => handleSelectionWithFirestore(index, 'adulthood', false)}
                            className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                              ${selections[index].adulthood === false ? 'bg-red-200 border-gray-600 border-2' : 'bg-white'} 
                              ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''} 
                              transition-all duration-300`}
                            disabled={!!activeRecording || !canEdit}
                            aria-label="Moderate or severe impairment present in adulthood: No"
                          >
                            <div className="px-0.5 justify-center items-center flex">
                              <div className="text-slate-700 text-sm font-semibold leading-tight">No</div>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex w-full gap-6">
                {/* Left side - Examples checklist */}
                <div className="w-1/3 flex flex-col gap-2">
                  
                  <div className="flex flex-col gap-2">
                    <label className="text-slate-700 text-sm font-semibold">
                      Examples
                    </label>
                    <div className="flex flex-col">
                      {(question.examplesOptions_Adulthood || []).map((example, i) => (
                        <label
                          key={i}
                          className="flex items-start gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="checkbox mt-1"
                            checked={selectedExamples[index].adulthood.has(example)}
                            onChange={() => handleCheckboxChange('adulthood', example)}
                            disabled={!canEdit}
                            aria-label={`Example: ${example}`}
                          />
                          <span className="text-slate-700 text-sm font-medium">
                            {example}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                </div>

                <div className="border-l border-gray-200 mx-2"></div>

                {/* Right side */}
                <div className="w-2/3 flex flex-col">
                  
                  {/* Clinician Notes */}
                    <div className="grow shrink-0 self-stretch flex-col justify-start items-start gap-2 inline-flex">
                    <label className="text-slate-700 text-sm font-semibold">
                      Clinical Notes
                    </label>
                    <DictationTextArea
                      ref={textAreaRefs.current[index]?.adulthood}
                      value={notes[index].adulthood}
                      onChange={(val) => { handleNoteChange(index, 'Adulthood', val); setHasChanges(true); }}
                      onBlur={() => handleNoteBlur(index, 'Adulthood')}
                      canEdit={canEdit}
                      disabled={!canEdit || (activeRecording && (activeRecording.index !== index || activeRecording.type !== 'adulthood'))}
                      ariaLabel="Clinician notes for adulthood"
                      minHeight="15rem"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CHILDHOOD */}
        <div className="flex mb-4">
          <div className="w-full text-left">
            <div className="w-full p-6 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-center gap-2 inline-flex">
              <div className="self-stretch flex-col justify-start items-center flex">
                <div className="self-stretch justify-start items-center gap-4 inline-flex">
                  <div className="grow shrink basis-0 self-stretch justify-start items-center gap-1 flex">
                    <div className="text-slate-700 text-xl font-semibold leading-loose">Childhood and Adolescence</div>
                  </div>
                  <div className="self-stretch justify-center items-center flex">
                    <div className="h-10 justify-start items-center flex">
                      <div className="grow shrink basis-0 h-9 justify-center items-center gap-3 flex">
                        <div className="flex-col justify-center items-center inline-flex">
                          <div className=" text-slate-700 text-lg font-medium leading-normal">Moderate or severe impairment:</div>
                        </div>
                        <div className="grow shrink basis-0 h-9 justify-start items-center gap-3 flex">
                        <button
                            onClick={() => handleSelectionWithFirestore(index, 'childhood', true)}
                            className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                              ${selections[index].childhood === true ? 'bg-green-200 border-gray-600 border-2' : 'bg-white'} 
                              ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''} 
                              transition-all duration-300`}
                            disabled={!!activeRecording || !canEdit}
                            aria-label="Moderate or severe impairment present in childhood: Yes"
                          >
                            <div className="px-0.5 justify-center items-center flex">
                              <div className="text-slate-700 text-sm font-semibold leading-tight">Yes</div>
                            </div>
                          </button>
                          <button
                            onClick={() => handleSelectionWithFirestore(index, 'childhood', false)}
                            className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex 
                              ${selections[index].childhood === false ? 'bg-red-200 border-gray-600 border-2' : 'bg-white'} 
                              ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''} 
                              transition-all duration-300`}
                            disabled={!!activeRecording || !canEdit}
                            aria-label="Moderate or severe impairment present in childhood: No"
                          >
                            <div className="px-0.5 justify-center items-center flex">
                              <div className="text-slate-700 text-sm font-semibold leading-tight">No</div>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

                <div className="flex w-full gap-6">
                  {/* Left side - Examples checklist */}
                  <div className="w-1/3 flex flex-col gap-2">
                                      
                    <div className="flex flex-col gap-2">
                      <label className="text-slate-700 text-sm font-semibold">
                        Examples
                      </label>
                      <div className="flex flex-col">
                        {(question.examplesOptions_Childhood || []).map((example, i) => (
                          <label
                            key={i}
                            className="flex items-start gap-2 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              className="checkbox mt-1"
                              checked={selectedExamples[index].childhood.has(example)}
                              onChange={() => handleCheckboxChange('childhood', example)}
                              disabled={!canEdit}
                              aria-label={`Example: ${example}`}
                            />
                            <span className="text-slate-700 text-sm font-medium">
                              {example}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                  </div>

                  <div className="border-l border-gray-200 mx-2"></div>

                  {/* Right side */}
                  <div className="w-2/3 flex flex-col">
                    
                    {/* Clinician Notes */}
                    <div className="grow shrink-0 self-stretch flex-col justify-start items-start gap-2 inline-flex">
                      <label className="text-slate-700 text-sm font-semibold">
                        Clinical Notes
                      </label>
                      <DictationTextArea
                        ref={textAreaRefs.current[index]?.childhood}
                        value={notes[index].childhood}
                        onChange={(val) => { handleNoteChange(index, 'Childhood', val); setHasChanges(true); }}
                        onBlur={() => handleNoteBlur(index, 'Childhood')}
                        canEdit={canEdit}
                        disabled={!canEdit || (activeRecording && (activeRecording.index !== index || activeRecording.type !== 'childhood'))}
                        ariaLabel="Clinician notes for childhood"
                        minHeight="15rem"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end mb-3">
            <div className="flex flex-col items-end">
              <button
                onClick={() => {
                  if (index === questions.length - 1) {
                    handleNavigateToNextSection();
                  } else {
                    jumpToQuestion(index + 1);
                  }
                }}
                disabled={!!activeRecording}
                className="flex items-center py-2 px-4 mb-1 rounded-md shadow-md transition duration-100 bg-violet-900 text-white hover:bg-violet-800"
                aria-label={index === questions.length - 1 ? nextSectionLabel : "Next Domain"}
              >
                <span className="font-bold">
                  {index === questions.length - 1 ? nextSectionLabel : "Next Domain"}
                </span>
                <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
              </button>
              {index < questions.length - 1 && (
                <button
                  onClick={handleNavigateToNextSection}
                  className="flex items-center text-xs text-gray-500 hover:text-violet-700 font-medium hover:underline transition-all duration-200 mt-1.5 group"
                  aria-label={nextSectionLabel}
                >
                  <span>{nextSectionLabel}</span>
                  <ArrowRightIcon className="w-3 h-3 ml-1 text-gray-400 group-hover:text-violet-700 transition-all duration-200" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        
        </div>

      {/* Validation Alert Dialog */}
      <AlertDialog.Root open={isValidationDialogOpen} onOpenChange={setIsValidationDialogOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <AlertDialog.Title className="text-xl font-bold text-gray-900">
                Clinical Decisions Required
              </AlertDialog.Title>
              <AlertDialog.Cancel asChild>
                <button className="absolute top-2 right-3 text-gray-500 hover:text-gray-700">
                  <span aria-hidden>×</span>
                </button>
              </AlertDialog.Cancel>
            </div>

            <AlertDialog.Description className="text-sm text-gray-600 mb-4">
              Some clinical decisions have not been made yet. Please review the missing decisions below.
            </AlertDialog.Description>

            <div className="mb-6">
              {validationResults.missingSymptoms.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold mb-2 text-gray-700">
                    {validationResults.missingSymptoms.length} Clinical Decision{validationResults.missingSymptoms.length === 1 ? '' : 's'} for Symptoms {validationResults.missingSymptoms.length === 1 ? 'is' : 'are'} missing:
                  </h3>
                  <div className="bg-red-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {validationResults.missingSymptoms.slice(0, 5).map((symptom, idx) => (
                      <div key={idx} className="text-sm text-red-800 mb-1">
                        <span className="font-medium">Symptom {symptom.dsmCode}:</span> Decision for symptom present in {" "}
                        {symptom.missingAdulthood && symptom.missingChildhood 
                          ? "adulthood and childhood" 
                          : symptom.missingAdulthood 
                            ? "adulthood" 
                            : "childhood"}
                      </div>
                    ))}
                    {validationResults.missingSymptoms.length > 5 && (
                      <div className="text-sm text-red-700 font-medium">
                        ...and {validationResults.missingSymptoms.length - 5} more symptoms
                      </div>
                    )}
                  </div>
                </div>
              )}

              {validationResults.missingDomains.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold mb-2 text-gray-700">
                    {validationResults.missingDomains.length} Clinical Decision{validationResults.missingDomains.length === 1 ? '' : 's'} for Domains {validationResults.missingDomains.length === 1 ? 'is' : 'are'} missing:
                  </h3>
                  <div className="bg-red-50 rounded-lg p-3">
                    {validationResults.missingDomains.map((domain, idx) => (
                      <div key={idx} className="text-sm text-red-800 mb-1">
                        <span className="font-medium">{domain.domain}:</span> Decision for impairment in{" "}
                        {domain.missingAdulthood && domain.missingChildhood 
                          ? "adulthood and childhood" 
                          : domain.missingAdulthood 
                            ? "adulthood" 
                            : "childhood"}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <p className="mb-6 text-sm italic text-amber-700 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <strong>Note:</strong> If you choose to proceed anyway, 
              {validationResults.missingSymptoms.length > 0 && validationResults.missingDomains.length > 0 
                ? " any missing clinical decisions for symptoms will be marked as not present and any missing clinical decisions for domains will be marked as not moderate or severe impairment."
                : validationResults.missingSymptoms.length > 0 
                  ? " any missing clinical decisions for symptoms will be marked as not present."
                  : " any missing clinical decisions for domains will be marked as not moderate or severe impairment."
              }
            </p>

            <div className="flex justify-end gap-3">
              {validationResults.missingSymptoms.length > 0 && (
                <button
                  onClick={navigateToFirstMissingSymptom}
                  className="py-2 px-4 mb-2 font-semibold rounded-md bg-violet-900 text-white hover:bg-violet-800"
                >
                  Go to Symptoms
                </button>
              )}
              
              {validationResults.missingDomains.length > 0 && (
                <button
                  onClick={navigateToFirstMissingDomain}
                  className="py-2 px-4 mb-2 font-semibold rounded-md bg-violet-900 text-white hover:bg-violet-800"
                >
                  Go to Domains
                </button>
              )}

              <button
                onClick={() => {
                  setIsValidationDialogOpen(false);
                  proceedToNextSection();
                }}
                className="font-semibold flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300"
              >
                Proceed Anyway
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
}
